import { attempt, get, isFunction } from "lodash";
import {
  openLocationRevalidate,
  setLocationRevalidateFailure
} from "@tvg/revalidate-location/actions";
import mediator from "@tvg/mediator";
import GeoComply from "@tvg/login-service/services/geoComply";
import TvgGeolocation from "@tvg/geolocation";
import {
  openLocationSplash,
  setLocationLoading,
  setLocationRequired,
  setLocationUnauthorized,
  setLocationUnknownError,
  setOnLocationGet
} from "@tvg/location-splash/actions";

import { requestLocation } from "@tvg/location-splash/splashBuilder";
import TVGConf from "@tvg/conf";
import isMobile from "@tvg/utils/mobileUtils";

import { getGeoLocation } from "@tvg/sh-lib-account-actions/src/actions/geo";

const Geolocation = new TvgGeolocation();
const geoComply = new GeoComply();
let geoTimeout = null;
const DEFAULT_TIMEOUT = 60000;
let previousTimeout;

export const getStatesWithLocationRequired = (message) =>
  message ? JSON.parse(message).map((state) => state.toUpperCase()) : [];

export const checkMobileLocation = (
  geolocation,
  payload,
  splashError,
  isLocationSplashOpen,
  isLocationRequired,
  dispatch,
  openLogin
) => {
  if (
    isMobile(TVGConf().product) &&
    (!get(geolocation, "location.latitude") ||
      !get(geolocation, "location.longitude") ||
      !get(geolocation, "state"))
  ) {
    mediator.ios.dispatch({ type: "ASK_LOCATION" });
  } else if (isMobile(TVGConf().product)) {
    TvgGeolocation.validateState(get(geolocation, "state"))
      .then((blacklisted) => {
        if (
          (blacklisted && splashError !== "BLOCKED_COUNTRY") ||
          (blacklisted &&
            splashError === "BLOCKED_COUNTRY" &&
            !isLocationSplashOpen &&
            !isLocationRequired)
        ) {
          dispatch(setLocationRequired(true));
          dispatch(setLocationUnauthorized());
          dispatch(openLocationSplash());
        }
        if (!blacklisted) {
          openLogin(payload);
        }
      })
      .catch(() => {
        dispatch(setLocationRequired(true));
        dispatch(setLocationUnknownError(true));
        dispatch(openLocationSplash());
      });
  } else {
    openLogin(payload);
  }
};

export const setNewTimeoutRevalidateLocation = (
  accountId,
  timeoutInterval,
  dispatch,
  geolocateInterval = null,
  retry = 0
) => {
  geoTimeout = setTimeout(() => {
    // eslint-disable-next-line
    revalidateLocation(geolocateInterval, accountId, retry, dispatch);
  }, timeoutInterval);
};

export const revalidateLocationError = (retry, accountId, dispatch) => {
  if (retry < 4) {
    setNewTimeoutRevalidateLocation(
      accountId,
      DEFAULT_TIMEOUT,
      dispatch,
      null,
      retry + 1
    );

    dispatch(openLocationRevalidate());
  } else {
    dispatch(setLocationRevalidateFailure());
    mediator.base.dispatch({
      type: "TVG_LOGIN:DO_LOGOUT"
    });
  }
};

export const removeTimeout = () => {
  if (geoTimeout !== null) {
    clearTimeout(geoTimeout);
  }
};

export const revalidateLocation = (
  geolocateInterval,
  accountId,
  retry = 0,
  dispatch
) => {
  if (geoTimeout !== null) {
    clearTimeout(geoTimeout);
  }

  if (geolocateInterval) {
    setNewTimeoutRevalidateLocation(accountId, geolocateInterval, dispatch);
  } else {
    geoComply
      .getGeoPacket(accountId, "revalidate")
      .then(({ geo }) => {
        geoComply
          .validateGeoPacket(accountId, geo)
          .then(({ data: { geolocateIn } }) => {
            const millisecondsGeolocateIn = geolocateIn * 1000;
            setNewTimeoutRevalidateLocation(
              accountId,
              millisecondsGeolocateIn,
              dispatch
            );
            previousTimeout = millisecondsGeolocateIn;
          })
          .catch(() => {
            revalidateLocationError(retry, accountId, dispatch);
          });
      })
      .catch((error) => {
        if (error.message === geoComply.softErrorMessage) {
          setNewTimeoutRevalidateLocation(accountId, previousTimeout, dispatch);
        } else {
          revalidateLocationError(retry, accountId, dispatch);
        }
      });
  }
};

export const handleRevalidateLocation = (user, geopacketUsage, dispatch) => {
  const geolocateIn = get(user, "geolocateIn");
  /*
   * geopacketUsage is a capi feat toggle to validate location for 4NJBets
   */ if (
    geopacketUsage &&
    user.logged &&
    geolocateIn &&
    TVGConf().brand === "4njbets"
  ) {
    revalidateLocation(
      geolocateIn * 1000,
      get(user, "user.accountNumber"),
      0,
      dispatch
    );
  }
};

export const requestLocationWeb = (user, props) => {
  if (!isMobile(TVGConf().product)) {
    const userState =
      get(user, "user.homeAddress.stateAbbr", null) ||
      get(user, "user.homeAddress.state", null) ||
      get(user, "user.homeState", null);
    const locationRequired =
      userState &&
      getStatesWithLocationRequired(get(props, "locationRequired")).includes(
        userState.toUpperCase()
      ) &&
      get(props, "history.location.pathname") !== "/redirectEngine";
    props.dispatch(setLocationRequired(locationRequired));
    if (locationRequired) {
      if (attempt(() => window.localStorage.getItem("shownLocationSplash"))) {
        requestLocation(props.dispatch, Geolocation, props.onLocationGet);
      } else {
        props.dispatch(openLocationSplash());
      }
    } else if (props.onLocationGet && isFunction(props.onLocationGet)) {
      props.onLocationGet();
      props.dispatch(setOnLocationGet(null));
    }
  }
};

export const handleIOSLocation = (state, callback, dispatch) => {
  TvgGeolocation.validateState(state)
    .then((blacklisted) => {
      if (blacklisted) {
        dispatch(setLocationRequired(true));
        dispatch(setLocationUnauthorized());
        dispatch(openLocationSplash());
      }
      if (callback) {
        callback(blacklisted);
      }
    })
    .catch(() => {
      dispatch(setLocationRequired(true));
      dispatch(setLocationUnknownError(true));
      dispatch(openLocationSplash());
    });
};

export const setGeoLocationData = (data, dispatch, onLocationGet) => {
  TvgGeolocation.setLocation(data.latitude, data.longitude).then(
    (locationData) => {
      dispatch(getGeoLocation({ ...locationData, state: get(data, "state") }));

      if (onLocationGet && isFunction(onLocationGet)) {
        onLocationGet(locationData);
        dispatch(setLocationLoading(false));
        dispatch(setOnLocationGet(null));
      }
    }
  );
};
